import { alertController } from '@ionic/core';
import { BaseDoc } from 'common';

export class SharedUtils {
  static scrollToBottom(ref: HTMLDivElement, delay = 100): void {
    if (ref) {
      setTimeout(() => {
        ref.scrollTop = ref.scrollHeight;
      }, delay);
    }
  }

  static async filterAndValidateUploads(files: FileList, currentUploads: File[]): Promise<File[]> {
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const validFiles = Array.from(files).filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(extension);
    });
    if (validFiles.length !== files.length) {
      const alert = await alertController.create({
        header: 'Invalid File Type',
        message: 'Only PDF, Word documents, Excel spreadsheets, and common image formats are allowed.',
        buttons: ['OK'],
      });
      await alert.present();
    }
    return [...currentUploads, ...validFiles];
  }

  static upsertArrayUtil<T extends { _id: string } | { id: string }>(targetArray: T[], itemsToUpsert: T[]): T[] {
    // filter out deleted items
    const result = [...targetArray];

    const getId = (item: T): string => {
      return '_id' in item ? item._id : (item as { id: string }).id;
    };

    itemsToUpsert.forEach(item => {
      const itemId = getId(item);
      const index = result.findIndex(existingItem => getId(existingItem) === itemId);

      if (index !== -1) {
        result[index] = { ...result[index], ...item };
      } else {
        result.push(item);
      }
    });

    return result;
  }

  static capitalizeText(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  /** Truncate a filename while preserving the final extension, e.g. `Very_Long_Name...pdf`. */
  static truncateFilename(filename: string, maxLength = 50): string {
    if (!filename || filename.length <= maxLength) return filename;

    const ellipsis = '...';
    const lastDot = filename.lastIndexOf('.');
    const hasExtension = lastDot > 0 && lastDot < filename.length - 1;
    const extension = hasExtension ? filename.slice(lastDot + 1) : '';
    const budget = maxLength - ellipsis.length - extension.length;

    if (!extension || budget < 1) {
      return `${filename.slice(0, Math.max(1, maxLength - ellipsis.length))}${ellipsis}`;
    }

    return `${filename.slice(0, budget)}${ellipsis}${extension}`;
  }
}
